import React from 'react';

/**
 * The enamel chip: a soft tint of the status color over the panel, a hairline
 * edge in the same hue, and the small offset shadow that makes it read as a
 * pressed object rather than a painted rectangle.
 *
 * Deliberately `rounded-md`, never `rounded-full`. Pill-shaped tinted chips are
 * the generic-dashboard tell this design rejects, and several places in this app
 * (Nav's sub-badge, DividerTabs' badge) still use pills and should adopt this.
 *
 * One chip carries exactly one status color. If everything on a screen is
 * colored, none of it reads as a signal.
 */
export type BadgeTone = 'green' | 'red' | 'amber' | 'blue' | 'neutral' | 'gold';

const toneClass: Record<BadgeTone, string> = {
  green: 'bg-primary/15 text-primary border-primary/40',
  red: 'bg-sell/15 text-sell border-sell/40',
  amber: 'bg-warn/15 text-warn border-warn/40',
  blue: 'bg-info/15 text-info border-info/40',
  neutral: 'bg-page-alt text-fg-3 border-soft',
  gold: 'bg-gold/15 text-gold border-gold/40',
};

export interface BadgeProps {
  tone?: BadgeTone;
  className?: string;
  title?: string;
  children?: React.ReactNode;
}

export const Badge = ({ tone = 'neutral', className, title, children }: BadgeProps) => (
  <span
    title={title}
    className={`badge inline-flex items-center gap-1 rounded-md border px-2 py-[3px] text-[11px] leading-none font-semibold shadow-chip ${toneClass[tone]}${className ? ` ${className}` : ''}`}
  >
    {children}
  </span>
);

/**
 * A bare status dot, for the rare case where a full chip is too heavy (a row of
 * many rows, a legend). Uses the same tone vocabulary so the two never diverge.
 */
const dotClass: Record<BadgeTone, string> = {
  green: 'bg-primary',
  red: 'bg-sell',
  amber: 'bg-warn',
  blue: 'bg-info',
  neutral: 'bg-dead',
  gold: 'bg-gold',
};

export const StatusDot = ({ tone = 'neutral', className }: { tone?: BadgeTone; className?: string }) => (
  <span className={`inline-block h-2 w-2 shrink-0 rounded-coin ${dotClass[tone]}${className ? ` ${className}` : ''}`} />
);
